import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

/**
 * FilterSidebar Component
 *
 * A slide-in sidebar panel for advanced filtering of data.
 * Supports multiple filter types with collapsible sections.
 */
export default function FilterSidebar({
  isOpen,
  onClose,
  onFiltersChange,
  filters = {},
  filterOptions = {},
  title = "Filters",
  className = "",
}) {
  const [activeFilters, setActiveFilters] = useState(filters);
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    type: true,
    other: true,
  });

  // Update local state when external filters change
  useEffect(() => {
    setActiveFilters(filters);
  }, [filters]);

  // Handle filter change
  const handleFilterChange = (filterType, value, checked) => {
    const currentValues = activeFilters[filterType] || [];
    let newValues;

    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter((v) => v !== value);
    }

    const updatedFilters = {
      ...activeFilters,
      [filterType]: newValues.length > 0 ? newValues : undefined,
    };

    // Remove undefined values
    Object.keys(updatedFilters).forEach((key) => {
      if (updatedFilters[key] === undefined) {
        delete updatedFilters[key];
      }
    });

    setActiveFilters(updatedFilters);

    if (onFiltersChange) {
      onFiltersChange(updatedFilters);
    }
  };

  // Handle section toggle
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setActiveFilters({});
    if (onFiltersChange) {
      onFiltersChange({});
    }
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters).reduce(
    (count, filterArray) => count + (filterArray?.length || 0),
    0
  );

  // Render filter section
  const renderFilterSection = (sectionKey, sectionTitle, options) => {
    if (!options || options.length === 0) return null;

    const isExpanded = expandedSections[sectionKey];
    const sectionFilters = activeFilters[sectionKey] || [];

    return (
      <div
        key={sectionKey}
        className="border-b border-gray-200 last:border-b-0"
      >
        <button
          onClick={() => toggleSection(sectionKey)}
          className={`
            w-full px-4 py-3 flex items-center justify-between
            text-left font-medium text-gray-900
            hover:bg-gray-50 transition-colors duration-200
          `}
        >
          <span className="flex items-center space-x-2">
            <span>{sectionTitle}</span>
            {sectionFilters.length > 0 && (
              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                {sectionFilters.length}
              </span>
            )}
          </span>
          {isExpanded ? (
            <ChevronUpIcon className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
          )}
        </button>

        {isExpanded && (
          <div className="px-4 pb-4 space-y-2">
            {options.map((option) => (
              <label
                key={option.value}
                className={`
                  flex items-center space-x-3 cursor-pointer
                  hover:bg-gray-50 rounded-md p-2 transition-colors duration-200
                `}
              >
                <input
                  type="checkbox"
                  checked={sectionFilters.includes(option.value)}
                  onChange={(e) =>
                    handleFilterChange(
                      sectionKey,
                      option.value,
                      e.target.checked
                    )
                  }
                  className={`
                    h-4 w-4 text-blue-600 border-gray-300 rounded
                    focus:ring-blue-500 focus:ring-2
                  `}
                />
                <span className="text-sm text-gray-700 flex-1">
                  {option.label}
                </span>
                {option.count !== undefined && (
                  <span className="text-xs text-gray-500">
                    ({option.count})
                  </span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed top-0 right-0 h-full w-80 bg-white shadow-lg z-50
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "translate-x-full"}
          ${className}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {activeFilterCount > 0 && (
              <span className="bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className={`
              p-1 rounded-md text-gray-400 hover:text-gray-600
              hover:bg-gray-100 transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-blue-500
            `}
            aria-label="Close filters"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Filter Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Status Filters */}
          {filterOptions.status &&
            renderFilterSection("status", "Status", filterOptions.status)}

          {/* Type Filters */}
          {filterOptions.type &&
            renderFilterSection("type", "Type", filterOptions.type)}

          {/* Additional Filters */}
          {filterOptions.priority &&
            renderFilterSection("priority", "Priority", filterOptions.priority)}

          {filterOptions.phase &&
            renderFilterSection("phase", "Phase", filterOptions.phase)}

          {filterOptions.location &&
            renderFilterSection("location", "Location", filterOptions.location)}

          {/* Custom filter sections */}
          {Object.entries(filterOptions).map(([key, options]) => {
            if (
              ["status", "type", "priority", "phase", "location"].includes(key)
            ) {
              return null;
            }
            return renderFilterSection(
              key,
              key.charAt(0).toUpperCase() + key.slice(1),
              options
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 space-y-3">
          <button
            onClick={clearAllFilters}
            disabled={activeFilterCount === 0}
            className={`
              w-full py-2 px-4 border border-gray-300 rounded-md text-sm
              font-medium transition-colors duration-200
              ${
                activeFilterCount > 0
                  ? "text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500"
                  : "text-gray-400 bg-gray-100 cursor-not-allowed"
              }
            `}
          >
            Clear All Filters
          </button>

          <div className="text-xs text-gray-500 text-center">
            {activeFilterCount === 0
              ? "No filters applied"
              : `${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} applied`}
          </div>
        </div>
      </div>
    </>
  );
}
